import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useGetDocuments } from "../hooks/document/useDocumentClient";
import { useDocumentDownload } from "../hooks/document/useDocumentDownload";
import { useToast } from "../contexts/ToastContext";
import AppLayout from "./layout/AppLayout";
import DocumentSigningModal from "./DocumentSigningModal";

let sharedFetchPromise = null;
let activeMounts = 0;

function Dashboard() {
  const { user, whoamiData } = useAuth();
  const getDocuments = useGetDocuments();
  const { showToast } = useToast();
  const { downloadUnsignedDocument, downloadSignedDocument, downloading } = useDocumentDownload();
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.dropdown-menu-container')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  useEffect(() => {
    if (!user) {
      return;
    }

    let isCancelled = false;
    activeMounts++;

    const fetchDocuments = async () => {
      try {
        const data = await getDocuments({}, {}, {});

        if (!data || !data.document_list) {
          throw new Error("Invalid response format: missing document_list array");
        }

        const transformedDocs = data.document_list.map((row) => ({
          id: row?.meta?.document_id,
          name: row?.meta?.display_name || row?.meta?.name || 'Untitled Document',
          description: row?.meta?.description || '',
          status: row?.sign_detail?.is_signed ? "Completed" : "Pending",
          signedBy: null,
          signedDate: null,
          meta: row?.meta,
        }));

        return transformedDocs;
      } catch (error) {
        throw error;
      }
    };

    if (sharedFetchPromise) {
      sharedFetchPromise
        .then((docs) => {
          if (!isCancelled && docs) {
            setDocuments(docs);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          if (!isCancelled) {
            showToast("Failed to load documents. Please try again.", "error");
            setDocuments([]);
            setIsLoading(false);
            sharedFetchPromise = null;
          }
        });
    } else {
      setIsLoading(true);
      sharedFetchPromise = fetchDocuments()
        .then((docs) => {
          if (!isCancelled && docs) {
            setDocuments(docs);
            setIsLoading(false);
          }
          return docs;
        })
        .catch((error) => {
          if (!isCancelled) {
            showToast("Failed to load documents. Please try again.", "error");
            setDocuments([]);
            setIsLoading(false);
          }
          throw error;
        });
    }

    return () => {
      isCancelled = true;
      activeMounts--;

      setTimeout(() => {
        if (activeMounts === 0 && sharedFetchPromise) {
          sharedFetchPromise = null;
        }
      }, 100);
    };
  }, [user, whoamiData, getDocuments, showToast]);

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-medium";
    if (status === "Completed") {
      return (
        <span className={`${baseClasses} bg-green-100 text-green-800`}>
          {status}
        </span>
      );
    } else if (status === "Pending") {
      return (
        <span className={`${baseClasses} bg-orange-100 text-orange-800`}>
          {status}
        </span>
      );
    }
    return (
      <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
        {status}
      </span>
    );
  };

  const handleSignDocument = (doc) => {
    setSelectedDocument(doc);
    setIsModalOpen(true);
  };

  const handleCloseModal = async (shouldRefresh) => {
    setIsModalOpen(false);
    setSelectedDocument(null);

    if (shouldRefresh) {
      try {
        setIsLoading(true);
        const data = await getDocuments({}, {}, {});
        const transformedDocs = data.document_list.map((row) => ({
          id: row?.meta?.document_id,
          name: row?.meta?.display_name || row?.meta?.name || 'Untitled Document',
          description: row?.meta?.description || '',
          status: row?.sign_detail?.is_signed ? "Completed" : "Pending",
          signedBy: null,
          signedDate: null,
          meta: row?.meta,
        }));
        setDocuments(transformedDocs);
      } catch (error) {
        showToast(
          "Failed to refresh documents. Please refresh the page.",
          "error"
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleDownloadUnsigned = async (e, doc) => {
    e.stopPropagation();
    try {
      await downloadUnsignedDocument(doc);
      showToast("Document downloaded successfully", "success");
    } catch (error) {
      showToast(error?.message || "Failed to download document", "error");
    }
  };

  const handleDownloadSigned = async (e, doc) => {
    e.stopPropagation();
    try {
      await downloadSignedDocument(doc);
      showToast("Signed document downloaded successfully", "success");
    } catch (error) {
      showToast(error?.message || "Failed to download signed document", "error");
    }
  };

  const toggleMenu = (e, docId) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === docId ? null : docId);
  };

  return (
    <AppLayout>
      <div className="w-full">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">
            My Documents
          </h1>
          <p className="text-sm text-gray-500">
            {documents.length}{" "}
            {documents.length === 1 ? "document" : "documents"}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              <p className="mt-4 text-sm text-gray-500">Loading documents...</p>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <svg
                className="mx-auto h-16 w-16 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-4 text-base font-medium text-gray-900">
                No documents found
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Your documents will appear here
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-200">
              {documents.map((doc, index) => (
                <div
                  key={doc.id}
                  onClick={() => handleSignDocument(doc)}
                  className="group flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded group-hover:bg-red-100 transition-colors">
                      <svg
                        className="w-6 h-6"
                        viewBox="0 0 56 56"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill="#E9E9E0"
                          d="M36.985,0H7.963C7.155,0,6.5,0.655,6.5,1.926V55c0,0.345,0.655,1,1.463,1h40.074c0.808,0,1.463-0.655,1.463-1V12.978c0-0.696-0.093-0.92-0.257-1.085L37.607,0.257C37.442,0.093,37.218,0,36.985,0z"
                        />
                        <polygon
                          fill="#D9D7CA"
                          points="37.5,0.151 37.5,12 49.349,12"
                        />
                        <path
                          fill="#CC4B4C"
                          d="M19.514,33.324L19.514,33.324c-0.348,0-0.682-0.113-0.967-0.326c-1.041-0.781-1.181-1.65-1.115-2.242c0.182-1.628,2.195-3.332,5.985-5.068c1.504-3.296,2.935-7.357,3.788-10.75c-0.998-2.172-1.968-4.99-1.261-6.643c0.248-0.579,0.557-1.023,1.134-1.215c0.228-0.076,0.804-0.172,1.016-0.172c0.504,0,0.947,0.649,1.261,1.049c0.295,0.376,0.964,1.173-0.373,6.802c1.348,2.784,3.258,5.62,5.088,7.562c1.311-0.237,2.439-0.358,3.358-0.358c1.566,0,2.515,0.365,2.902,1.117c0.32,0.622,0.189,1.349-0.39,2.16c-0.557,0.779-1.325,1.191-2.22,1.191c-1.216,0-2.632-0.768-4.211-2.285c-2.837,0.593-6.15,1.651-8.828,2.822c-0.836,1.774-1.637,3.203-2.383,4.251C21.273,32.654,20.389,33.324,19.514,33.324z M22.176,28.198c-2.137,1.201-3.008,2.188-3.071,2.744c-0.01,0.092-0.037,0.334,0.431,0.692C19.685,31.587,20.555,31.19,22.176,28.198z M35.813,23.756c0.815,0.627,1.014,0.944,1.547,0.944c0.234,0,0.901-0.01,1.21-0.441c0.149-0.209,0.207-0.343,0.23-0.415c-0.123-0.065-0.286-0.197-1.175-0.197C37.12,23.648,36.485,23.67,35.813,23.756z M28.343,17.174c-0.715,2.474-1.659,5.145-2.674,7.564c2.09-0.811,4.362-1.519,6.496-2.02C30.815,21.15,29.466,19.192,28.343,17.174z M27.736,8.712c-0.098,0.033-1.33,1.757,0.096,3.216C28.781,9.813,27.779,8.698,27.736,8.712z"
                        />
                        <path
                          fill="#CC4B4C"
                          d="M48.037,56H7.963C7.155,56,6.5,55.345,6.5,54.537V39h43v15.537C49.5,55.345,48.845,56,48.037,56z"
                        />
                        <g>
                          <path
                            fill="#FFFFFF"
                            d="M17.385,53h-1.641V42.924h2.898c0.428,0,0.852,0.068,1.271,0.205c0.419,0.137,0.795,0.342,1.128,0.615c0.333,0.273,0.602,0.604,0.807,0.991s0.308,0.822,0.308,1.306c0,0.511-0.087,0.973-0.26,1.388c-0.173,0.415-0.415,0.764-0.725,1.046c-0.31,0.282-0.684,0.501-1.121,0.656s-0.921,0.232-1.449,0.232h-1.217V53z M17.385,44.168v3.992h1.504c0.2,0,0.398-0.034,0.595-0.103c0.196-0.068,0.376-0.18,0.54-0.335c0.164-0.155,0.296-0.371,0.396-0.649c0.1-0.278,0.15-0.622,0.15-1.032c0-0.164-0.023-0.354-0.068-0.567c-0.046-0.214-0.139-0.419-0.28-0.615c-0.142-0.196-0.34-0.36-0.595-0.492c-0.255-0.132-0.593-0.198-1.012-0.198H17.385z"
                          />
                          <path
                            fill="#FFFFFF"
                            d="M32.219,47.682c0,0.829-0.089,1.538-0.267,2.126s-0.403,1.08-0.677,1.477s-0.581,0.709-0.923,0.937s-0.672,0.398-0.991,0.513c-0.319,0.114-0.611,0.187-0.875,0.219C28.222,52.984,28.026,53,27.898,53h-3.814V42.924h3.035c0.848,0,1.593,0.135,2.235,0.403s1.176,0.627,1.6,1.073s0.74,0.955,0.95,1.524C32.114,46.494,32.219,47.08,32.219,47.682z M27.352,51.797c1.112,0,1.914-0.355,2.406-1.066s0.738-1.741,0.738-3.09c0-0.419-0.05-0.834-0.15-1.244c-0.101-0.41-0.294-0.781-0.581-1.114s-0.677-0.602-1.169-0.807s-1.13-0.308-1.914-0.308h-0.957v7.629H27.352z"
                          />
                          <path
                            fill="#FFFFFF"
                            d="M36.266,44.168v3.172h4.211v1.121h-4.211V53h-1.668V42.924H40.9v1.244H36.266z"
                          />
                        </g>
                      </svg>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                        {doc.name}
                      </h3>
                      {getStatusBadge(doc.status)}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {doc.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {doc.signedDate && (
                      <span className="text-xs text-gray-400 hidden lg:block">
                        {formatDate(doc.signedDate)}
                      </span>
                    )}

                    <button
                      onClick={(e) => handleDownloadUnsigned(e, doc)}
                      disabled={downloading}
                      className="hidden xl:inline-flex items-center gap-1.5 px-3 py-1.5 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download unsigned document"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                      <span className="hidden sm:inline">Download</span>
                    </button>
                    {doc.status === "Completed" && (
                      <button
                        onClick={(e) => handleDownloadSigned(e, doc)}
                        disabled={downloading}
                        className="hidden xl:inline-flex items-center gap-1.5 px-3 py-1.5 text-green-600 text-sm font-medium rounded-md hover:bg-green-50 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Download signed document"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        <span className="hidden sm:inline">Download Signed</span>
                      </button>
                    )}

                    {/* Action buttons - always visible */}
                    {doc.status === "Pending" ? (
                      doc.meta?.is_signable !== false ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSignDocument(doc);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 transition-colors"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                          Sign Document
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSignDocument(doc);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 text-sm font-medium hover:text-blue-700 hover:underline transition-colors"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                          View Document
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSignDocument(doc);
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 text-sm font-medium hover:text-blue-700 hover:underline transition-colors cursor-pointer bg-transparent border-0 font-inherit"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        View Signed Document
                      </button>
                    )}

                    <div className="xl:hidden relative dropdown-menu-container">
                      <button
                        onClick={(e) => toggleMenu(e, doc.id)}
                        className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        title="Download options"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                      </button>

                      {openMenuId === doc.id && (
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                          <div className="py-1">
                            <button
                              onClick={(e) => handleDownloadUnsigned(e, doc)}
                              disabled={downloading}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                              Download
                            </button>
                            {doc.status === "Completed" && (
                              <button
                                onClick={(e) => handleDownloadSigned(e, doc)}
                                disabled={downloading}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-green-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                  />
                                </svg>
                                Download Signed
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <DocumentSigningModal
        isOpen={isModalOpen}
        onClose={(shouldRefresh = false) => handleCloseModal(shouldRefresh)}
        document={selectedDocument}
      />
    </AppLayout>
  );
}

export default Dashboard;
